import { useMemo, useState } from 'react'
import { Card, HStack, IconButton, Lightbox, Text, VStack } from 'wss3-forge'
import {
  Checkmark16Regular,
  Copy16Regular,
  Document16Regular,
  Settings16Regular,
} from '@fluentui/react-icons'
import { Markdown } from './Markdown.js'
import type { StoredAttachment } from '../lib/chat-storage.js'

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`
}

// Inject the assistant-message hover-action stylesheet once globally instead of
// per-message; long threads otherwise leak N identical <style> nodes.
if (typeof document !== 'undefined' && !document.getElementById('stark-msg-styles')) {
  const styleEl = document.createElement('style')
  styleEl.id = 'stark-msg-styles'
  styleEl.textContent = `
    .stark-msg-assistant .stark-msg-actions { opacity: 0; transition: opacity 150ms ease; }
    .stark-msg-assistant:hover .stark-msg-actions,
    .stark-msg-assistant:focus-within .stark-msg-actions { opacity: 1; }
    @media (hover: none) { .stark-msg-assistant .stark-msg-actions { opacity: 1; } }
  `
  document.head.appendChild(styleEl)
}

export interface ChatMessageProps {
  role: 'user' | 'assistant'
  content: string
  toolCalls?: { name: string }[]
  attachments?: StoredAttachment[]
}

/**
 * Chat-bubble layout, Claude-style.
 *
 * - User: right-aligned chip with a neutral elevated surface (no brand tint).
 *   The author is implied by alignment; color is reserved for emphasis.
 * - Assistant: no bubble. Content takes the column width; a discreet sparkle
 *   sits inline at the start so the eye lands on the content. Hover reveals a
 *   copy button.
 *
 * Markdown is rendered with `react-markdown` + `remark-gfm`.
 */
export function ChatMessage({ role, content, toolCalls, attachments }: ChatMessageProps) {
  const isUser = role === 'user'
  const [copied, setCopied] = useState(false)
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(content)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1400)
    } catch {
      // ignore
    }
  }

  // Lightbox state for inline image previews. Only images that still have
  // their in-memory dataUrl are previewable (post-reload images fall back
  // to the named-chip layout). The index points into `lightboxImages`.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const lightboxImages = useMemo(
    () =>
      (attachments ?? [])
        .filter((a) => a.mime.startsWith('image/') && !!a.dataUrl)
        .map((a) => ({ src: a.dataUrl!, alt: a.name, title: a.name })),
    [attachments],
  )

  if (isUser) {
    const hasAttachments = !!attachments && attachments.length > 0
    return (
      <VStack gap="xs" align="end" fullWidth>
        <div style={{ maxWidth: 'min(72ch, 85vw)' }}>
          <VStack gap="xs" align="end">
            {content.trim().length > 0 && (
              <Card variant="outlined" padding="sm">
                <Text style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{content}</Text>
              </Card>
            )}
            {hasAttachments && (
              <HStack gap="xs" wrap justify="end">
                {attachments!.map((a, i) => {
                  const isImage = a.mime.startsWith('image/')
                  // Image with an in-memory dataUrl: render the actual
                  // picture as a self-contained tile, no surrounding chip
                  // chrome. After a reload the dataUrl is gone (stripped
                  // by saveMessages to keep localStorage small), so we
                  // fall back to the named-chip layout below.
                  if (isImage && a.dataUrl) {
                    // Map this attachment's index in the full list to its
                    // index in the lightbox-eligible list (images only).
                    const lightboxIdx = lightboxImages.findIndex((li) => li.src === a.dataUrl)
                    return (
                      <button
                        key={`${a.name}-${i}`}
                        type="button"
                        onClick={() => lightboxIdx >= 0 && setLightboxIndex(lightboxIdx)}
                        title={`${a.name} (${formatBytes(a.size)})`}
                        aria-label={`Agrandir ${a.name}`}
                        style={{
                          display: 'block',
                          padding: 0,
                          maxWidth: 280,
                          maxHeight: 280,
                          borderRadius: 'var(--radius-md)',
                          overflow: 'hidden',
                          border: '1px solid var(--border-color)',
                          background: 'transparent',
                          lineHeight: 0,
                          cursor: 'zoom-in',
                        }}
                      >
                        <img
                          src={a.dataUrl}
                          alt={a.name}
                          style={{
                            display: 'block',
                            maxWidth: '100%',
                            maxHeight: 280,
                            objectFit: 'contain',
                            backgroundColor: 'var(--bg-secondary)',
                          }}
                        />
                      </button>
                    )
                  }
                  return (
                    <Card key={`${a.name}-${i}`} variant="outlined" padding="xs">
                      <HStack gap="xs" align="center">
                        <span
                          aria-hidden="true"
                          style={{
                            display: 'inline-flex',
                            color: 'var(--text-muted)',
                            flexShrink: 0,
                          }}
                        >
                          <Document16Regular />
                        </span>
                        <VStack gap="none" align="start">
                          <Text size="xs" style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {a.name}
                          </Text>
                          <Text size="xs" color="muted">
                            {formatBytes(a.size)}
                          </Text>
                        </VStack>
                      </HStack>
                    </Card>
                  )
                })}
              </HStack>
            )}
          </VStack>
        </div>
        {lightboxIndex !== null && lightboxImages.length > 0 && (
          <Lightbox
            images={lightboxImages}
            currentIndex={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onNavigate={setLightboxIndex}
          />
        )}
      </VStack>
    )
  }

  // Assistant
  return (
    <div className="stark-msg-assistant" style={{ position: 'relative' }}>
      <HStack gap="sm" align="start" fullWidth>
        {/* Discreet brand dot in lieu of an icon: keeps assistant messages
            anchored visually to the brand without adding chrome. The
            marginTop nudges the dot to align with the cap-height of the
            first markdown line (paragraphs, lists, headings all read at
            ~22px line height in Markdown body styles). */}
        <div
          aria-hidden="true"
          style={{
            flexShrink: 0,
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'var(--brand-primary)',
            marginTop: 10,
          }}
        />
        <VStack gap="xs" align="start" fullWidth style={{ minWidth: 0, flex: 1 }}>
          {toolCalls && toolCalls.length > 0 && (
            <HStack gap="xs" wrap align="center">
              {toolCalls.map((tc, i) => (
                <HStack key={`${tc.name}-${i}`} gap="xs" align="center">
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-flex',
                      color: 'var(--text-muted)',
                    }}
                  >
                    <Settings16Regular />
                  </span>
                  <Text size="xs" color="muted">
                    {tc.name}
                  </Text>
                </HStack>
              ))}
            </HStack>
          )}
          <div
            style={{
              maxWidth: 'min(72ch, 100%)',
              color: 'var(--text-primary)',
            }}
          >
            <Markdown source={content} />
          </div>
          <div className="stark-msg-actions">
            <IconButton
              icon={copied ? <Checkmark16Regular /> : <Copy16Regular />}
              tooltip={copied ? 'Copié' : 'Copier la réponse'}
              aria-label={copied ? 'Copié' : 'Copier la réponse'}
              variant="ghost"
              size="sm"
              onClick={onCopy}
            />
          </div>
        </VStack>
      </HStack>
    </div>
  )
}
